import { useTranslation } from 'react-i18next';
import { type AxiosError } from 'axios';

interface ApiErrorMessageProps {
  error: Error | AxiosError | null;
  onRetry?: () => void;
}

/**
 * De LEESBARE zin achter een mislukte aanroep, los van de rode melding eromheen.
 *
 * De serverlaag schrijft zelf een nette zin — "Vul voor beide stappen een norm in minuten per stuk
 * in" — en die staat in het antwoord, niet in `error.message`. Dat laatste is de tekst van de
 * netwerklaag ("Request failed with status code 400") en die zegt een lezer niets.
 *
 * Apart gezet zodat een scherm dat zijn eigen melding schrijft dezelfde zin kan pakken zonder de
 * rode doos eromheen mee te nemen (codereviewbevinding 29 augustus 2026).
 */
export function apiFoutmelding(error: Error | AxiosError | null): { serverMessage?: string; messageKey: string } {
  const axiosError = error as AxiosError<{ error?: { message?: string; code?: string } }> | null;
  const status = axiosError?.response?.status;
  const serverMessage = axiosError?.response?.data?.error?.message;

  let messageKey = 'errors.generic';
  if (!axiosError?.response) {
    messageKey = 'errors.network';
  } else if (status === 403) {
    messageKey = 'errors.forbidden';
  } else if (status === 404) {
    messageKey = 'errors.notFound';
  } else if (status && status >= 500) {
    messageKey = 'errors.serverError';
  }

  return { ...(serverMessage === undefined ? {} : { serverMessage }), messageKey };
}

export function ApiErrorMessage({ error, onRetry }: ApiErrorMessageProps) {
  const { t, i18n } = useTranslation();

  if (!error) return null;

  const { serverMessage, messageKey } = apiFoutmelding(error);

  return (
    <div
      data-testid="api-error"
      className="flex items-start gap-2.5 rounded-[10px] p-3 px-4 mb-4"
      style={{
        backgroundColor: 'var(--destructive-light)',
        border: '1px solid rgba(214, 69, 69, 0.25)',
      }}
    >
      <span
        className="material-symbols-outlined shrink-0"
        style={{ fontSize: '16px', color: 'var(--destructive)' }}
        aria-hidden="true"
      >
        error_outline
      </span>
      <div className="flex-1">
        <p
          className="text-xs font-semibold"
          style={{ color: 'var(--destructive)' }}
        >
          {/* Een serversleutel (bijv. intake.klantOnbekend) wordt vertaald, nooit als ruwe sleutel getoond. */}
          {serverMessage && i18n.exists(serverMessage) ? t(serverMessage) : serverMessage || t(messageKey)}
        </p>
      </div>
      {onRetry && (
        <button
          data-testid="error-retry"
          onClick={onRetry}
          className="shrink-0 rounded-lg px-3 py-1 text-xs font-medium"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
        >
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}
